import { Component, Input, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EvidenceCitationLinkService, CitationPresentation } from './evidence-citation-link.service';
import { EVIDENCE_KIND_LABEL, EvidenceCitation, EvidenceCitationKind } from './evidence-citation.model';

/**
 * One inline source citation inside a report body (Issue #1773).
 *
 * Deliberately an Angular component rather than an anchor spliced into sanitized HTML. A component
 * can hold a `routerLink`, a tooltip, and an accessible name that the template controls, whereas
 * markup injected through `[innerHTML]` cannot carry an Angular directive at all and would have to
 * be a bare `href` — which is how a report ends up navigating with a full page reload, losing the
 * reader's place, and how a model-authored URL slips into the same shape as a verified one.
 *
 * Rendering is driven entirely by a {@link CitationPresentation} computed once from the citation, so
 * change detection never rebuilds a `routerLink` array. That matters here for the same reason it did
 * in {@link FilePageLinkService}: a report body can hold dozens of citations, and rebuilding arrays
 * per cycle is what makes a page feel sticky under the mouse.
 *
 * The three visual states map exactly onto the three destination kinds the link service produces.
 * An internal destination is an in-application router link; an external one opens a new tab with
 * safe attributes; an unlinkable one renders as visible, non-interactive text that explains itself,
 * because a citation a reader cannot follow is still evidence they are entitled to see.
 */
@Component({
  selector: 'app-evidence-citation-chip',
  imports: [CommonModule, RouterModule, MatIconModule, MatTooltipModule],
  templateUrl: './evidence-citation-chip.component.html',
  styleUrls: ['./evidence-citation-chip.component.scss'],
})
export class EvidenceCitationChipComponent {
  private readonly linkService = inject(EvidenceCitationLinkService);

  private readonly citationSignal = signal<EvidenceCitation | null>(null);
  private readonly markerIndexSignal = signal(1);
  private readonly activeFileIdSignal = signal('');
  private readonly activePageNumberSignal = signal(0);

  /** The resolved citation to render. */
  @Input({ required: true })
  set citation(value: EvidenceCitation) {
    this.citationSignal.set(value);
  }

  /** One-based position of this citation in the report, shown as the inline marker. */
  @Input()
  set markerIndex(value: number) {
    this.markerIndexSignal.set(value);
  }

  /** File whose report is open, used only to decide whether this citation leads elsewhere. */
  @Input()
  set activeFileId(value: string) {
    this.activeFileIdSignal.set(value);
  }

  /** Page whose report is open, used only to decide whether this citation leads elsewhere. */
  @Input()
  set activePageNumber(value: number) {
    this.activePageNumberSignal.set(value);
  }

  /**
   * How this citation is displayed.
   *
   * `marker` sits in the prose as a superscript-style number and stays out of the way, which is what
   * the issue means by "unobtrusive". `full` is the expanded form used by the sources list beneath a
   * report and by print output, where a bare number would be useless.
   *
   * `inline-text` (Issue #1773) is for a code reference the prose already names: it renders the
   * section number itself as the link, with no icon and no badge, so the sentence reads exactly as
   * written and the number a reader recognizes is the text they click. There is nothing to disclose
   * that the wording does not already say, which is why it carries no adornment.
   */
  @Input() display: 'marker' | 'full' | 'inline-text' = 'marker';

  /** Everything the template binds, recomputed only when an input actually changes. */
  readonly presentation = computed<CitationPresentation | null>(() => {
    const citation = this.citationSignal();
    if (!citation) return null;
    return this.linkService.present(
      citation,
      this.markerIndexSignal(),
      this.activeFileIdSignal(),
      this.activePageNumberSignal()
    );
  });

  /** Stable semantic identity used by deployed report-consumption journeys. */
  readonly citationId = computed(() => this.citationSignal()?.id ?? '');

  /** Stable evidence kind used by deployed report-consumption journeys. */
  readonly citationKind = computed(() => this.citationSignal()?.kind ?? '');

  /** Material icon for each evidence kind, so the kind is legible without reading the label. */
  readonly iconName = computed<string>(() => {
    const kind = this.citationSignal()?.kind;
    switch (kind) {
      case 'region':
        return 'crop_free';
      case 'text':
        return 'format_quote';
      case 'code':
        return 'menu_book';
      default:
        return 'description';
    }
  });

  /** Short kind label, e.g. `Plan object`. */
  readonly kindLabel = computed<string>(() => {
    const kind: EvidenceCitationKind | undefined = this.citationSignal()?.kind;
    return kind ? EVIDENCE_KIND_LABEL[kind] : '';
  });

  /**
   * Whether this citation's evidence was inferred rather than verified.
   *
   * Surfaced as its own visual state, not merely buried in the tooltip. The issue requires inferred
   * evidence never to be dressed up as verified, and a reader scanning a report should not have to
   * hover every marker to discover which claims rest on a guess.
   */
  readonly isInferred = computed<boolean>(() => this.citationSignal()?.provenance.method === 'inferred');
}
